import { default as _get } from 'lodash.get';
import { default as _isArray } from 'lodash.isarray';

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { DatePipe } from '@angular/common';
import { saveAs as importedSaveAs } from 'file-saver';
import { AppMemoryService } from 'app/core/app-memory.service';
import { TranslateService } from '@ngx-translate/core';

interface TableColValue {
  key: string[];
  suffix?: string;
  preffix?: string;
  alternative?: any;
  type?: string;
}

interface TableColsType {
  title: string;
  values: TableColValue[];
  type?: string;
  className?: string;
  sort?: {
    active: boolean,
    key: string,
    order?: boolean
  };
  translate?: boolean;
}

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.less']
})
export class ReportComponent implements OnInit {
  @Input() data: any[];
  @Input() title: string;
  @Input() total: number;

  @Output() load = new EventEmitter<String>();
  @Output() sort = new EventEmitter<Object>();

  public tableCols: TableColsType[] = [] as TableColsType[];
  public sortCriteria: { field: string, order: boolean } = {} as { field: string, order: boolean };

  constructor(private datePipe: DatePipe, private appMemory: AppMemoryService, private translateService: TranslateService) {
    this.datePipe = new DatePipe(translateService.currentLang);
  }

  ngOnInit() {
    this.getHeaderItems();
  }

  /**
   * Creates the list of columns
   */
  getHeaderItems() {
    if (this.data && this.data.length) {
      switch (this.title) {
        case 'notes':
          this.tableCols = [
            { title: 'HORSES.horse', type: 'string', values: [
              { key: ['horse', 'name'] }
            ] , sort: { active: true, key: 'horse_id' }, translate: false },
            { title: 'CORE.yard', type: 'string', values: [
              { key: ['horse', 'farm', 'name'] }
            ], translate: false },
            { title: 'HORSES.dateOfNote', type: 'date', values: [
              { key: ['date'], alternative: ['created_at'] }
            ] , sort: { active: false, key: 'date'}, translate: false },
            { title: 'HORSES.noteType', type: 'string', values: [
              { key: ['type'] }
            ] , sort: { active: true, key: 'type' }, translate: true },
            { title: 'CORE.title', type: 'string', values: [
              { key: ['title'] }
            ] , sort: { active: true, key: 'title' }, translate: false },
            { title: 'HORSES.description', type: 'string', values: [
              { key: ['description'] }
            ] , sort: { active: true, key: 'description' }, translate: false },
            { title: 'CORE.attachment', type: 'download', values: [
              { key: ['images'] }
            ] , sort: { active: true, key: 'images' }, translate: false }
          ];
          break;
        case 'vaccinations':
          this.tableCols = [
            { title: 'HORSES.horse', type: 'string', values: [
              { key: ['horse', 'name'] }
            ], translate: false },
            { title: 'CORE.yard', type: 'string', values: [
              { key: ['horse', 'farm', 'name'] }
            ], translate: false },
            { title: 'HORSES.vaccinationName', type: 'string', values: [
              { key: ['name'] }
            ] , sort: { active: true, key: 'name' }, translate: true },
            { title: 'HORSES.vaccinationNumber', type: 'string', values: [
              { key: ['number'] }
            ] , sort: { active: true, key: 'number' }, translate: true },
            { title: 'HORSES.administrationDate', type: 'date', values: [
              { key: ['date'] }
            ] , sort: { active: true, key: 'date' } },
            { title: 'HORSES.dateofNextAdmin', values: [
                { key: [ 'immunization' ], type: 'date', alternative: ['deadline'] },
                { key: [ 'vaccination_country' ] },
              ]
            }
          ];
          break;
        case 'treatments':
          this.tableCols = [
            { title: 'HORSES.horse', type: 'string', values: [
              { key: ['horse', 'name'] }
            ] , sort: { active: true, key: 'horse_id' }, translate: false },
            { title: 'CORE.yard', type: 'string', values: [
              { key: ['horse', 'farm', 'name'] }
            ], translate: false },
            { title: 'HORSES.startDate', type: 'date', values: [
              { key: ['start_date'] }
            ] , sort: { active: true, key: 'start_date' }, translate: false },
            { title: 'HORSES.endDate', type: 'date', values: [
              { key: ['withdrawal'] }
            ] , sort: { active: true, key: 'withdrawal' }, translate: false },
            { title: 'CORE.status', type: 'string', values: [
              { key: ['status'] }
            ] , sort: { active: true, key: 'status' }, translate: true },
            { title: 'TREATS.treatment', type: 'string', values: [
              { key: ['drug_name'] },
              { key: ['reason'], preffix: 'HORSES.reason' }
            ], className: 'block', translate: false },
            { title: 'HORSES.person', type: 'string', values: [
              { key: ['request', 'user', 'name'], alternative: this.appMemory.user.name },
              { key: ['request', 'user', 'last_name'], alternative: this.appMemory.user.last_name }
            ], className: 'inline', translate: false },
            { title: 'HORSES.withdrawal', type: 'string', values: [
              { key: ['duration'], suffix: 'HORSES.days' }
            ], translate: false }
          ];
          break;

        default:
          break;
      }
    }

  }

  /**
   * Find value according to the keys
   * @param {*} list
   * @param {TableColsType} col
   * @returns {({value: (string | number | undefined), suffix: string, preffix: string}[])}
   */
  nestedProperties(list: any, col: TableColsType):
    {value: (string | number | undefined), suffix: string, preffix: string, translate?: boolean}[] {
    const result: {
      value: (string | number | undefined),
      suffix: string, preffix: string,
      translate?: boolean}[] = [] as {value: (string | number | undefined), suffix: string, preffix: string, translate?: boolean}[];

      for (let i = 0; i < col.values.length; i++) {
        const element: TableColValue = col.values[i];
        let finalValue: any =  _get(list, element.key.join('.'));

        finalValue = !finalValue && element.alternative
          ? _isArray(element.alternative)
            ? _get(list, element.alternative.join('.'))
            : element.alternative
          : finalValue;

        if (finalValue && col.type) {
          finalValue = col.type === 'date'
            ? this.datePipe.transform(new Date(finalValue.split(' ')[0]), 'dd MMM y').replace(/\./, '')
            : col.type === 'string'
              ? finalValue.toString()
              : _isArray(finalValue)
                ? finalValue.filter(value => !!value)
                : finalValue;
        }

      result.push({ value: finalValue, suffix: element.suffix, preffix: element.preffix, translate: col.translate });
    }
    return result;
  }

  /**
   * Load more items
   * @param {*} property
   */
  loadMore(property): void {
    this.load.emit(property);
  }

  /**
   * Sort the column by clicked column header
   * @param {*} item
   * @memberof ReportComponent
   */
  sortColumn(item): void {
    if (item && item.active) {
      const column = this.tableCols.find(el => el.sort && el.sort.key === item.key);
      if (column) {
        column.sort.order = !column.sort.order;
      }
      this.sortCriteria.field = item.key;
      this.sortCriteria.order = column.sort.order;
      this.sort.emit({field: this.sortCriteria.field, order: this.sortCriteria.order ? 'asc' : 'desc'});
    }
  }

  downloadAttach(link, fileName: string): void {
    importedSaveAs(link, fileName);
  }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/emr-webapp/src/app/custom/admin-report/components/report/report.component.ts